/**
 * TOOLS — the capabilities that live in CODE, not in the database (MAR-487).
 *
 * A tool is a fixed capability with no configuration surface: converting an image to Word is the
 * same operation for every organization, forever, and the only way to change it is a ticket and a
 * deploy. That is the whole distinction from a WORKFLOW, which is user-configurable, multi-step and
 * — as of this change — created only by uploading a document template.
 *
 * WHY THIS FILE EXISTS AT ALL, given "tools have no database entity". Two different things were
 * conflated before:
 *
 *   * a tool's DEFINITION — its id, what it accepts, what it does. Code. Never a row.
 *   * a tool's EXECUTION — who ran it, when, against which file, what came out, did it fail. Data,
 *     and necessarily so: it takes minutes, it is dispatched to a queue, and the Activity Center and
 *     the chat card both have to be able to find it again after a reload.
 *
 * MAR-476 got this wrong in the other direction. It made the two converters unlisted rows in
 * `workflow_definitions` — fake definitions whose only purpose was to give a `workflow_runs` row
 * something to point at. The definition was invented to satisfy the execution; here the execution
 * gets its own table and the definition stays in code, which is the right way round.
 */

/**
 * Every tool, by id. The union IS the roster — there is no registry table to read and no seed to
 * run, so adding a tool means adding a member here and an entry in `server/tools/registry.ts`.
 *
 * These ids reach the database (`tool_runs.tool_id`) and the model (the `tools` skill names them),
 * so they are stable identifiers rather than display strings: renaming one is a migration.
 */
export const TOOL_IDS = ['image_to_word', 'pdf_to_word'] as const
export type ToolId = typeof TOOL_IDS[number]

export function isToolId(value: unknown): value is ToolId {
  return typeof value === 'string' && (TOOL_IDS as readonly string[]).includes(value)
}

/**
 * What a tool reads. A tool accepts exactly ONE source kind — the image tool cannot read a PDF and
 * the PDF tool cannot read an image — which is what lets the model pick by the user's file rather
 * than by how they phrased the request.
 */
export const TOOL_SOURCE_KINDS = ['image', 'pdf'] as const
export type ToolSourceKind = typeof TOOL_SOURCE_KINDS[number]

/**
 * A run's lifecycle.
 *
 * Deliberately NOT `WorkflowRunStatus`' five values. That vocabulary carries `cancelled` and spells
 * its middle state `executing`, both of which are properties of a multi-step engine that can be
 * interrupted between steps. A tool run is one indivisible call: it is waiting, it is in flight, or
 * it has settled one way or the other. Copying the workflow vocabulary would have imported two
 * states nothing can ever produce, and a state no reader could trust is worse than no state.
 *
 * Mirrored by the `tool_runs_status_check` constraint — this union is the source of truth for it.
 */
export const TOOL_RUN_STATUSES = ['pending', 'running', 'succeeded', 'failed'] as const
export type ToolRunStatus = typeof TOOL_RUN_STATUSES[number]

/** The settled statuses — a run in one of these will never change again. */
export const TERMINAL_TOOL_RUN_STATUSES: readonly ToolRunStatus[] = ['succeeded', 'failed']

export function isTerminalToolRunStatus(status: ToolRunStatus): boolean {
  return TERMINAL_TOOL_RUN_STATUSES.includes(status)
}

/**
 * Why a run failed, as a code the UI can act on rather than a string it can only print.
 *
 * `unsupported_source` is the one that is NOT a malfunction: it means the file was not the kind this
 * tool reads, which is a complete and correct answer to give the user, not an error to retry.
 */
export const TOOL_RUN_ERROR_CODES = [
  'unsupported_source',
  'source_unreadable',
  'conversion_failed',
  'internal',
] as const
export type ToolRunErrorCode = typeof TOOL_RUN_ERROR_CODES[number]

export interface ToolRunError {
  code: ToolRunErrorCode
  /** Model- and log-facing, English. The UI renders its own copy from `code`. */
  message: string
}

/**
 * What a tool LOOKS LIKE — the half of a descriptor that carries no code.
 *
 * It lives in `shared/` rather than beside the executors because the browser needs it and must not
 * fetch it. Under MAR-476 the chat card had to resolve a converter's name over the network before it
 * could render, which is why `ConvertDocumentRequest` carried a `loading` flag and blank strings:
 * the roster was in the database, so the client could not know it. The roster is code now, so the
 * card can render its final text on the first paint and there is no endpoint to list tools at all.
 *
 * `label` is user-facing and therefore Spanish. The model-facing English `description` stays in
 * `server/tools/registry.ts` with the executor — the browser has no use for it, and prompts are not
 * shipped to clients.
 */
export interface ToolPresentation {
  label: string
  /**
   * One sentence for a person, in Spanish — NOT the model-facing `description` in
   * `server/tools/registry.ts`, which is English and tells a model when to pick the tool. The two
   * audiences want different sentences, so they are different fields rather than one translated.
   */
  summary: string
  accepts: ToolSourceKind
}

export const TOOL_PRESENTATION: Record<ToolId, ToolPresentation> = {
  image_to_word: {
    label: 'Convertir imagen a Word',
    summary: 'Toma una foto o un escaneo de un documento (JPG o PNG) y lo reconstruye como un archivo Word editable. El archivo original no se modifica.',
    accepts: 'image',
  },
  pdf_to_word: {
    label: 'Convertir PDF a Word',
    summary: 'Toma un PDF, nativo o escaneado, y lo reconstruye como un archivo Word editable. El archivo original no se modifica.',
    accepts: 'pdf',
  },
}

/** The roster in display order — what the Herramientas tab renders. No endpoint: the list is code. */
export const TOOL_LIST: ReadonlyArray<{ id: ToolId } & ToolPresentation> =
  TOOL_IDS.map(id => ({ id, ...TOOL_PRESENTATION[id] }))

/**
 * One execution, as it goes over the wire.
 *
 * Timestamps are ISO strings: a modelled `timestamptz` comes back from Drizzle as a `Date`, and JSON
 * turns it into a string, so the client view and the server view genuinely differ. `D` is what keeps
 * that honest instead of a `Date` wearing a `string` type — the mistake `workflow_runs` made once.
 */
export interface ToolRunFields<D> {
  id: string
  tool_id: ToolId
  status: ToolRunStatus
  /** The `items.id` the tool read. */
  source_item_id: string
  /**
   * The source document's name, joined in rather than fetched.
   *
   * The Activity Center's contract requires the PRODUCER to resolve everything domain-specific
   * before a task reaches the panel, so a row that says only "Convertir imagen a Word" with no
   * filename is not renderable. Joining costs one query; resolving it client-side would cost one
   * request per run in the list.
   */
  source_item_name: string
  /** The `items.id` it produced. Null until the run succeeds, and forever on a failed run. */
  output_item_id: string | null
  /** Null whenever `output_item_id` is, and also if that document was later deleted. */
  output_item_name: string | null
  error: ToolRunError | null
  conversation_id: string | null
  /**
   * The chat message whose conversion card started this run. What puts the card's result back on
   * the right message after a reload (MAR-991) — a thread can convert several files.
   */
  message_id: string | null
  case_id: string | null
  started_at: D
  completed_at: D | null
  duration_ms: number | null
}

/** As the service reads it from the database. */
export type ToolRunRow = ToolRunFields<Date>
/** As the client receives it. */
export type ToolRun = ToolRunFields<string>

/** `POST /api/tools/run` and `GET /api/tools/runs/:id`. */
export interface ToolRunResponse { run: ToolRun }

/** `GET /api/tools/runs?conversationId=` — the caller's runs started from one conversation. */
export interface ToolRunListResponse { runs: ToolRun[] }
